/* The serving route: one read of the deployment's boundaries, one read of the tenant's route, one PUT.
 *
 * **A PUT and no POST, and no `Idempotency-Key`.** The natural key is the tenant, so a route is
 * replaced rather than created and a duplicate request writes the same row — which is why
 * `shared/api/http.ts` deliberately gives `put` no idempotency parameter while `post` requires one.
 * Reaching for `post` here would be asking for a key the server has no use for.
 *
 * The read is invalidated after the write rather than patched optimistically. The write can be
 * refused — a hosted backend for an air-gapped tenant is a 422 from the domain, and a non-admin is a
 * 403 — so the authoritative answer is the one the server just stored. An optimistic patch would show
 * a route that does not exist, on the screen whose entire subject is where a tenant's metadata
 * actually goes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isServingCapabilities,
  isServingRouteView,
} from '@/shared/api/generated/ai-serving';
import type {
  ServingBackend,
  ServingCapabilities,
  ServingRouteView,
} from '@/shared/api/generated/ai-serving';
import { getOne, put } from '@/shared/api/http';

export const ROUTE_URL = '/v1/ai/serving/route';
export const CAPABILITIES_URL = '/v1/ai/serving/capabilities';

const ROUTE_KEY = ['ai', 'serving', 'route'] as const;
const CAPABILITIES_KEY = ['ai', 'serving', 'capabilities'] as const;

/** What the caller intends the route to be. Every field, because a PUT replaces the whole route. */
export interface RouteWrite {
  readonly backend: ServingBackend;
  readonly endpoint: string;
  readonly model: string;
  readonly air_gapped: boolean;
  readonly enabled: boolean;
}

export function useServingRoute(): UseQueryResult<ServingRouteView> {
  return useQuery({
    queryKey: ROUTE_KEY,
    queryFn: async () => getOne(ROUTE_URL, isServingRouteView, 'serving route'),
    retry: false,
  });
}

export function useServingCapabilities(): UseQueryResult<ServingCapabilities> {
  return useQuery({
    queryKey: CAPABILITIES_KEY,
    queryFn: async () =>
      getOne(CAPABILITIES_URL, isServingCapabilities, 'serving capabilities'),
    /* The deployment's own boundaries — rung, what is never sent, the validator policy — do not
     * change while somebody is looking at them, so this is read once per session rather than per
     * mount. It also means a 429 on the route read does not cost the reader the paragraph explaining
     * what the AI plane is permitted to see. */
    staleTime: Infinity,
    retry: false,
  });
}

export function useConfigureRoute(): UseMutationResult<
  ServingRouteView,
  Error,
  RouteWrite
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (write: RouteWrite) =>
      put(ROUTE_URL, isServingRouteView, 'serving route', write),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ROUTE_KEY });
    },
  });
}

export type { ServingBackend, ServingCapabilities, ServingRouteView };
